import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { useXHR } from "@sembark-travel/xhr"
import React, { useMemo } from "react"
import { TouristProfileForm, TTouristProfile } from "../TouristProfiles"
import { ITouristFormData } from "./NewItem"
import { parseDate } from "@sembark-travel/datetime-utils"
import { transformAddressToEditableValue } from "../Addresses"
import { TTourist } from "../Tourists"

type TouristFormData = ITouristFormData["tourists"][number]

type Props<T> = {
	tourist: T
	mapToInitialValues: (
		tourist: T
	) => React.ComponentProps<typeof TouristProfileForm>["initialValues"]
	successMessage: string
	onSuccess: (touristProfile: TTouristProfile) => void
	onCancel: () => void
}

function AddTouristProfile<T>({
	tourist,
	mapToInitialValues,
	successMessage,
	onSuccess,
	onCancel,
}: Props<T>) {
	const xhr = useXHR()

	const initialValues = useMemo(
		() => mapToInitialValues(tourist),
		[tourist, mapToInitialValues]
	)

	return (
		<TouristProfileForm
			initialValues={initialValues}
			onSubmit={async (values) => {
				const touristProfile = await xhr.post("/tourist-profiles", values)
				showSnackbar(successMessage)
				onSuccess(touristProfile.data.data)
				return touristProfile
			}}
			onCancel={onCancel}
		/>
	)
}

function touristDataToInitialValues(tourist: TTourist) {
	const {
		id,
		type,
		salutation,
		name_without_salutation,
		dob,
		nationality,
		phone_numbers,
		email,
		address,
	} = tourist

	return {
		type: type ?? "adult",
		salutation: salutation ?? undefined,
		name: name_without_salutation ?? "",
		dob: dob ? parseDate(dob) : undefined,
		nationality: nationality ?? undefined,
		phone_numbers: (phone_numbers ?? []).map((p) => ({
			country_code: p.country_code ?? "",
			phone_number: p.phone_number ?? "",
			number: p.number ?? "",
			is_primary: p.is_primary,
			has_whatsapp: p.has_whatsapp,
		})),
		email: email ?? undefined,
		address: address ? transformAddressToEditableValue(address) : undefined,

		tourist_id: id ?? null,
		meal_preference: undefined,
		stay_preference: undefined,
		language_preference: undefined,
		preferred_airport: undefined,
		preferred_flight_class: "",
		marriage_anniversary: "",
		is_smoker: null,
		tags: [],
		passport_number: "",
		passport_expiry_date: undefined,
		company_name: "",
		gst_number: "",
		remarks: "",
	}
}

function touristFormDataToInitialValues(tourist: TouristFormData) {
	const {
		id,
		type,
		salutation,
		name,
		dob,
		nationality,
		phone_numbers,
		email,
		address,
		remarks,
	} = tourist

	return {
		type: type ?? "adult",
		salutation: salutation ?? undefined,
		name: name ?? "",
		dob: dob ?? undefined,
		nationality: nationality ?? undefined,
		phone_numbers: phone_numbers ?? [],
		email: email ?? undefined,
		address: address ?? undefined,

		tourist_id: id ?? null,
		meal_preference: undefined,
		stay_preference: undefined,
		language_preference: undefined,
		preferred_airport: undefined,
		preferred_flight_class: "",
		marriage_anniversary: "",
		is_smoker: null,
		tags: [],
		passport_number: "",
		passport_expiry_date: undefined,
		company_name: "",
		gst_number: address?.billing_details,
		remarks: remarks,
	}
}

export function AddTouristProfileDialog({
	tourist,
	onSuccess,
	children,
	isAdditionalInfo = false,
}: {
	tourist: TTourist | TouristFormData
	onSuccess: (touristProfile: TTouristProfile) => void
	children: (props: { show: () => void }) => React.ReactNode
	isAdditionalInfo?: boolean
}) {
	const [isOpen, open, close] = useDialog()

	return (
		<>
			{children({ show: open })}

			<Dialog
				open={isOpen}
				onClose={close}
				title={isAdditionalInfo ? "Add Additional Info" : "Add Tourist Profile"}
				lg
			>
				<Dialog.Body>
					{isAdditionalInfo ? (
						<AddTouristProfile
							tourist={tourist as TTourist}
							mapToInitialValues={touristDataToInitialValues}
							successMessage="Additional details added successfully."
							onSuccess={(touristProfile) => {
								onSuccess(touristProfile)
								close()
							}}
							onCancel={close}
						/>
					) : (
						<AddTouristProfile
							tourist={tourist as TouristFormData}
							mapToInitialValues={touristFormDataToInitialValues}
							successMessage="Tourist Profile added successfully."
							onSuccess={(touristProfile) => {
								onSuccess(touristProfile)
								close()
							}}
							onCancel={close}
						/>
					)}
				</Dialog.Body>
			</Dialog>
		</>
	)
}
